import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import useSopState, { ISop } from '@/states/sop.state';
import type { IJobDescription } from '@/states/job.state';
import { validateSopMethodTable } from './sop-form.schema';

type SopMethodItem = { _key: number; method: string; man: string; machine: string; material: string };

type Props = {
  jobDesc: IJobDescription;
  existingSop: ISop | null;
  localSopName: string;
  tenantId: string | number;
  isReadOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
};

function DynamicCellInput({
  value,
  onChange,
  disabled,
  maxLength,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative w-full">
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        maxLength={maxLength}
        placeholder={isFocused ? placeholder : ''}
        aria-label={ariaLabel}
        onFocus={() => setIsFocused(true)}
        onBlur={e => {
          e.target.scrollTop = 0;
          setIsFocused(false);
        }}
        rows={isFocused ? 3 : 1}
        className={
          'w-full text-xs sm:text-sm px-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 block py-1.5 resize-none ' +
          (isFocused ? 'min-h-[72px] overflow-auto relative z-10 shadow-sm' : 'h-9 overflow-hidden')
        }
        style={{ color: isFocused ? 'inherit' : 'transparent' }}
      />
      {!isFocused && (
        <div className="absolute inset-0 pointer-events-none px-3 flex items-center overflow-hidden text-xs sm:text-sm">
          <span className="truncate w-full text-gray-900 dark:text-white">
            {value || <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>}
          </span>
        </div>
      )}
    </div>
  );
}

export default function SOPEditModal({
  jobDesc,
  existingSop,
  localSopName,
  tenantId,
  isReadOnly,
  onClose,
  onSaved,
}: Props) {
  const { createSop, updateSop } = useSopState();
  const [isSaving, setIsSaving] = useState(false);
  const [sopName, setSopName] = useState(localSopName || existingSop?.name || '');

  const [rows, setRows] = useState<SopMethodItem[]>(() => {
    const items = (existingSop?.sop ?? existingSop?.org_sop ?? []) as Array<Record<string, unknown>>;
    if (items.length === 0) {
      return [{ _key: Date.now(), method: '', man: '', machine: '', material: '' }];
    }
    return items.map((item, i) => ({
      _key: i,
      method: typeof item.method === 'string' ? item.method : '',
      man: typeof item.man === 'string' ? item.man : '',
      machine: typeof item.machine === 'string' ? item.machine : '',
      material: typeof item.material === 'string' ? item.material : '',
    }));
  });

  const handleAddRow = () => {
    setRows(prev => [...prev, { _key: Date.now(), method: '', man: '', machine: '', material: '' }]);
  };

  const handleDeleteRow = (key: number) => {
    setRows(prev => {
      const next = prev.filter(r => r._key !== key);
      return next.length > 0 ? next : [{ _key: Date.now(), method: '', man: '', machine: '', material: '' }];
    });
  };

  const handleChange = (key: number, field: keyof Omit<SopMethodItem, '_key'>, value: string) => {
    setRows(prev => prev.map(r => (r._key === key ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    const trimmedName = sopName.trim();
    if (!trimmedName) {
      toast.error('Nama SOP wajib diisi');
      return;
    }
    const validationError = validateSopMethodTable(rows);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = {
      name: trimmedName,
      org_chart_note_id: jobDesc.org_chart_node_id,
      org_job_description_id: jobDesc.id,
      is_published: existingSop?.is_published ?? false,
      sop: rows.map(({ _key: _k, ...rest }) => {
        void _k;
        return rest;
      }),
    };

    setIsSaving(true);
    try {
      if (existingSop?.id) {
        await updateSop(tenantId, existingSop.id, payload);
      } else {
        await createSop(tenantId, payload);
      }
      onSaved();
    } catch {
      toast.error('Gagal menyimpan SOP');
    } finally {
      setIsSaving(false);
    }
  };

  const thClass =
    'px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide text-left border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-5xl lg:max-w-6xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Kelola SOP: {sopName || <span className="text-gray-400 dark:text-gray-600 font-normal">Tanpa Nama</span>}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-white dark:bg-gray-900">
          {/* Context */}
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Jabatan: {jobDesc.org_chart_node?.name ?? '-'}&nbsp;|&nbsp;Tugas: {jobDesc.name ?? '-'}
          </p>

          {/* Nama SOP input (only in edit mode) */}
          {!isReadOnly && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Nama SOP <span className="text-red-500">*</span>
              </label>
              <input
                value={sopName}
                onChange={e => setSopName(e.target.value)}
                maxLength={150}
                disabled
                placeholder="Masukkan nama SOP"
                className="w-full h-9 px-3 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed focus:outline-none"
                data-testid="sop-name-input"
              />
            </div>
          )}

          {/* 4M Table */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className={thClass}>Metode (What)</th>
                  <th className={thClass}>Manusia (Who)</th>
                  <th className={thClass}>Mesin/Alat</th>
                  <th className={thClass}>Material/Bahan</th>
                  <th className="px-3 py-3 w-12 text-center border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row._key}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/40 dark:hover:bg-gray-700/40 align-middle"
                  >
                    {(['method', 'man', 'machine', 'material'] as const).map(field => (
                      <td key={field} className="p-2 align-middle">
                        <DynamicCellInput
                          value={row[field]}
                          onChange={val => handleChange(row._key, field, val)}
                          disabled={isReadOnly}
                          maxLength={255}
                          placeholder={
                            field === 'method'
                              ? 'Metode'
                              : field === 'man'
                              ? 'Manusia'
                              : field === 'machine'
                              ? 'Mesin/Alat'
                              : 'Material'
                          }
                          ariaLabel={`${field}-${idx}`}
                        />
                      </td>
                    ))}
                    <td className="p-3 text-center align-middle">
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => handleDeleteRow(row._key)}
                          aria-label={`Hapus baris ${idx + 1}`}
                          className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add row button */}
          {!isReadOnly && (
            <button
              type="button"
              onClick={handleAddRow}
              className="flex items-center gap-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Plus size={14} />
              Tambah Baris
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Batal
          </button>
          {!isReadOnly && (
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-5 py-2 text-sm font-semibold rounded-lg text-white transition-opacity disabled:opacity-60"
              style={{ background: '#1848FD' }}
            >
              {isSaving ? 'Menyimpan...' : 'Simpan SOP 4M'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
